import { ErrorTracking } from '../tracking/error-tracking.service';
import {
  DeleteRequestPayload,
  PushRequestPayload,
  SetRequestPayload,
  UpdateRequestPayload,
} from './actions';
import { toNumber } from './helpers';
import { RootModel, RootModelSearch } from './root.model';
import { getQueryParamValue, pluckItemFromRawStore } from './store.helpers';
import { RawQuery, Reference, StoreState } from './store.model';

import { ThreadsGetResultApi } from './api-typings/messages/messages_types';
import { ProjectsGetResultApi } from './api-typings/projects/projects';
import { UsersGetResultApi } from './api-typings/users/users';
import { BidsUpdateActionRawPayload } from './bids/bids.backend-model';
import {
  MessagesPostApiResponse,
  MessagesPostRawPayload,
} from './messages/messages.backend-model';
import {
  NotificationsPreferencePostRawPayload,
  NotificationsPreferenceResultAjax,
} from './notifications-preferences/notifications-preferences.backend-model';
import { SearchHistoryPostRawPayload } from './search-history/search-history.backend-model';
import {
  SearchContestResultAjax,
  SearchFreelancersResultAjax,
  SearchKeywordProjectsResultAjax,
} from './search/search.model';
import {
  Thread,
  ThreadsPostApiResponse,
  ThreadsPostRawPayload,
  ThreadsUpdateActionRawPayload,
} from './thread/thread.model';

export interface Params {
  [key: string]:
    | string
    | number
    | (string | undefined)[]
    | (number | undefined)[];
}

export interface BackendConfig {
  endpoint: string;
  isGaf?: boolean;
  params: Params;
}

type FetchRequest<K extends keyof RootModel> = ((
  key1: string,
  key2: string[] | undefined,
  query?: RawQuery<RootModel[K]>,
) => BackendConfig);

type PostRequest<K extends keyof RootModel> = ((
  key: string,
  entity: RootModel[K],
) => BackendConfig);

export interface Backend<K extends keyof RootModel> {
  fetch?: FetchRequest<K>;
  post?: PostRequest<K>;
}

export interface BackendSearch {
  fetch: ((query: string) => BackendConfig);
}

export const backend: { [K in keyof RootModel]?: Backend<K> } = {
  threads: {
    fetch: () => ({
      endpoint: `messages/0.1/threads`,
      params: {
        unread_count: 'true',
        last_message: 'true',
        context_details: 'true',
        user_details: 'true',
        user_read_times: 'true',
      },
    }),
    post: () => ({
      endpoint: `messages/0.1/threads/`,
      params: {},
    }),
  },
  threadProjects: {
    fetch: (_, projectIds) => ({
      endpoint: `projects/0.1/projects`,
      params: {
        user_details: 'true',
        user_avatar: 'true',
        projects: projectIds || [],
      },
    }),
  },
  users: {
    fetch: (_, userIds, query) => ({
      endpoint: `users/0.1/users`,
      params: {
        support_status_details: 'true',
        avatar: 'true',
        users: userIds || [],
        usernames: query ? getQueryParamValue(query, 'username') : [],
      },
    }),
  },
};

export const backendSearch: { [K in keyof RootModelSearch]: BackendSearch } = {
  searchContests: {
    fetch: q => ({
      endpoint: `search/contests.php`,
      isGaf: true,
      params: { q },
    }),
  },
  searchKeywordProjects: {
    fetch: q => ({
      endpoint: `search/keywordProjects.php`,
      isGaf: true,
      params: { q },
    }),
  },
  searchFreelancers: {
    fetch: q => ({
      endpoint: `search/topFreelancer.php`,
      isGaf: true,
      params: { q },
    }),
  },
};

// Maps from datastore collection to corresponding backend fetch response
export interface BackendAjaxFetchTypes {
  searchContests: SearchContestResultAjax;
  searchFreelancers: SearchFreelancersResultAjax;
  searchKeywordProjects: SearchKeywordProjectsResultAjax;
}

export interface BackendApiFetchTypes {
  // ThreadSupportSessions: SupportSessionApi;
  threadProjects: ProjectsGetResultApi;
  threads: ThreadsGetResultApi;
  users: UsersGetResultApi;
}

export type BackendFetchTypes = BackendAjaxFetchTypes & BackendApiFetchTypes;

export type BackendPushPayloadTypes = {
  messages: MessagesPostRawPayload;
  searchHistory: any; // TODO
  threads: ThreadsPostRawPayload;
} & { [k: string]: never };

export type BackendPushReturnTypes = {
  messages: MessagesPostApiResponse;
  searchHistory: any; // TODO
  threads: ThreadsPostApiResponse;
} & { [k: string]: never };

export type BackendSetPayloadTypes = {
  notificationsPreferences: NotificationsPreferencePostRawPayload;
} & { [k: string]: never };

export type BackendSetReturnTypes = {
  notificationsPreferences: NotificationsPreferenceResultAjax;
} & { [k: string]: never };

export type BackendUpdatePayloadTypes = {
  threads: ThreadsUpdateActionRawPayload;
  bids: BidsUpdateActionRawPayload;
} & { [k: string]: never };

export type BackendUpdateReturnTypes = {} & { [k: string]: never };

export type BackendDeletePayloadTypes = {} & { [k: string]: never };

export type BackendDeleteReturnTypes = {} & { [k: string]: never };

export function pushPayload<K extends keyof RootModel>(
  reference: Reference<K>,
  object: RootModel[K],
  errorTracking: ErrorTracking,
): PushRequestPayload<K> | undefined {
  const type = reference.path[0];
  switch (type) {
    case 'threads': {
      const ref = reference;
      const thread = object as RootModel['threads'];
      const rawRequest: ThreadsPostRawPayload = {
        context: thread.context ? thread.context.id : undefined,
        context_type: thread.context ? thread.context.type : undefined,
        thread_type: thread.threadType,
        members: thread.members,
        message: (thread.message && thread.message.message) || '',
        source: 21,
      };
      return { type, ref, object: thread, rawRequest };
    }
    case 'messages': {
      const ref = reference;
      const message = object as RootModel['messages'];
      const rawRequest: MessagesPostRawPayload = {
        message: message.message,
        client_message_id: message.clientMessageId
          ? String(message.clientMessageId)
          : String(Date.now()),
        source: message.messageSource,
      };
      return { type, ref, object: message, rawRequest };
    }
    case 'searchHistory': {
      const ref = reference;
      const data = object as RootModel['searchHistory'];
      const rawRequest: SearchHistoryPostRawPayload = {
        keyword: data.keyword,
      };
      return { type, ref, object: data, rawRequest };
    }

    default:
      errorTracking.captureMessage(`Cannot push for ${type}.`, {
        extra: { reference, object },
      });
      return undefined;
  }
}

export function setPayload<K extends keyof RootModel>(
  reference: Reference<K>,
  id: string | number,
  object: RootModel[K],
  store: StoreState,
  errorTracking: ErrorTracking,
): SetRequestPayload<K> | undefined {
  const type = reference.path[0];

  switch (type) {
    case 'notificationsPreferences': {
      const ref = reference;
      const originalObject = pluckItemFromRawStore(store, ref.path, id);
      if (originalObject === undefined) {
        return undefined;
      }
      const notificationPreference = object as RootModel['notificationsPreferences'];
      const rawRequest: NotificationsPreferencePostRawPayload = {
        settings: JSON.stringify([notificationPreference]),
      };
      return {
        type,
        ref,
        object: notificationPreference,
        rawRequest,
        originalObject,
      };
    }

    default:
      errorTracking.captureMessage(`Cannot push for ${type}.`, {
        extra: { reference, object },
      });
      return undefined;
  }
}

export function updatePayload<K extends keyof RootModel>(
  reference: Reference<K>,
  id: string | number,
  object: Partial<RootModel[K]>,
  store: StoreState,
  errorTracking: ErrorTracking,
):
  | {
      payload: UpdateRequestPayload<K>;
      endpoint: string;
      method: 'POST' | 'PUT';
    }
  | undefined {
  const type = reference.path[0];
  const captureMessageError = { extra: { reference, object } };
  let method: 'PUT' | 'POST' = 'PUT';

  switch (type) {
    case 'threads': {
      const ref = reference;
      const originalObject = pluckItemFromRawStore(store, ref.path, id);
      if (originalObject === undefined) {
        return undefined;
      }
      const originalThread = originalObject as Thread;
      const thread = (object as any) as Partial<RootModel['threads']>;
      let rawRequest: ThreadsUpdateActionRawPayload | undefined;
      let endpoint: string | undefined;

      if (thread.isRead === true) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = {
          threads: [toNumber(id)],
          action: 'read',
        };
        endpoint = `messages/0.1/threads/`;
      }

      if (thread.isMuted !== undefined) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = {
          threads: [toNumber(id)],
          action: thread.isMuted ? 'mute' : 'unmute',
        };
        endpoint = `messages/0.1/threads/`;
      }

      if (thread.folder) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }

        if (thread.folder === 'inbox') {
          rawRequest = { threads: [toNumber(id)], action: 'unarchive' };
        } else if (thread.folder === 'archived') {
          rawRequest = { threads: [toNumber(id)], action: 'archive' };
        } else {
          errorTracking.captureMessage(
            `Cannot push for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        endpoint = `messages/0.1/threads/`;
      }

      if (
        thread.members &&
        thread.members.length !== originalThread.members.length
      ) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        endpoint = `messages/0.1/threads/${originalThread.id}/members/`;
        if (thread.members.length > originalThread.members.length) {
          rawRequest = {
            thread_id: originalThread.id,
            action: 'add',
            members: thread.members.filter(
              m => !originalThread.members.includes(m),
            ),
          };
        } else if (thread.members.length < originalThread.members.length) {
          rawRequest = {
            thread_id: originalThread.id,
            action: 'remove',
            members: originalThread.members.filter(
              m => !(thread.members || []).includes(m),
            ),
          };
        }
      }

      if (thread.typing) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = { thread_id: originalThread.id };
        endpoint = `messages/0.1/threads/${originalThread.id}/typing/`;
        method = 'POST';
      }
      if (rawRequest === undefined || endpoint === undefined) {
        errorTracking.captureMessage(
          `Cound not update any fields of ${type}.`,
          captureMessageError,
        );
        return undefined;
      }

      return {
        payload: {
          type,
          ref,
          object: thread as any,
          rawRequest,
          originalObject,
        },
        endpoint,
        method,
      };
    }
    case 'bids': {
      const ref = reference;
      const originalObject = pluckItemFromRawStore(store, ref.path, id);
      if (originalObject === undefined) {
        return undefined;
      }
      const bid = (object as any) as Partial<RootModel['bids']>;
      if (bid.awardStatus === 'awarded') {
        const endpoint = `projects/0.1/bids/${id}`;
        const rawRequest: BidsUpdateActionRawPayload = {
          bid_id: toNumber(id),
          action: 'accept',
        };
        return {
          payload: {
            type,
            ref,
            object: bid as any,
            rawRequest,
            originalObject,
          },
          endpoint,
          method,
        };
      }
      return undefined;
    }
    default:
      errorTracking.captureMessage(
        `Cannot update for ${type}.`,
        captureMessageError,
      );
      return undefined;
  }
}

export function deletePayload<K extends keyof RootModel>(
  reference: Reference<K>,
  id: string | number,
  store: StoreState,
  errorTracking: ErrorTracking,
):
  | {
      payload: DeleteRequestPayload<K>;
      endpoint: string;
      method: 'POST' | 'PUT' | 'DELETE';
    }
  | undefined {
  const type = reference.path[0];
  const captureMessageError = { extra: { reference } };

  switch (type) {
    default:
      errorTracking.captureMessage(
        `Cannot delete for ${type}.`,
        captureMessageError,
      );
      return undefined;
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/backend.ts